import { getPageImage } from "@/lib/page-imagery";
import type { FaqItem } from "@/components/FaqSection";
import type { Lang } from "@/lib/content";
import type { ArticleImage } from "@/components/insights/ArticleEditorial";

type Bi = Record<Lang, string>;

export interface VendorStep {
  title: Bi;
  text: Bi;
}

export interface VendorFact {
  sourceUrl?: string;
  label: Bi;
  value: Bi;
}

export interface VendorSection {
  heading: Bi;
  /** Each paragraph opens with its answer so it stands alone when extracted. */
  paragraphs: Bi[];
  links?: { label: Bi; to: string }[];
}

export interface VendorSource {
  label: Bi;
  url: string;
}

export interface VendorOperator {
  image?: ArticleImage;
  keyPoints?: Bi[];
  slug: string;
  name: Bi;
  metaTitle: Bi;
  title: Bi;
  description: Bi;
  /** BLUF intro paragraph. */
  intro: Bi;
  /** Platform the operator onboards suppliers through, if known. */
  platform?: { name: string; note: Bi };
  steps: VendorStep[];
  /** Typical documents (framed as "typically", never definitive). */
  documents: Bi[];
  /** Potentially relevant standards; applicability depends on the buyer and activity. */
  isoNeeded: string[];
  /** Explains any buyer-specific limits on the standards linked below. */
  isoNote?: Bi;
  /** schema.org areaServed; defaults to Iraq + Kurdistan when omitted. */
  areaServed?: Array<{ type: "Country" | "Place"; name: string }>;
  /** Key numbers rendered as a table right after the intro. */
  facts?: VendorFact[];
  /** Fact-dense narrative sections rendered before the process steps. */
  sections?: VendorSection[];
  /** Overrides the generic "Documents typically required" heading. */
  documentsTitle?: Bi;
  /** Primary sources cited at the end of the page. */
  sources?: VendorSource[];
  faq: FaqItem[];
}

// Shared building blocks reused across operators (kept honest and general).
const commonDocs: Bi[] = [
  { en: "Company legal registration", ar: "السجل القانوني للشركة" },
  { en: "Financial and technical capability evidence", ar: "أدلة القدرة المالية والفنية" },
  { en: "HSE policy and documentation", ar: "سياسة ووثائق الصحة والسلامة والبيئة" },
  { en: "Insurance evidence where requested", ar: "إثبات التأمين عند طلبه" },
  {
    en: "Relevant ISO certificates where the buyer or category requires them",
    ar: "شهادات الأيزو ذات الصلة عندما يشترطها المشتري أو فئة النشاط",
  },
];

const isoFaq: FaqItem = {
  q: {
    en: "Do I need ISO certification to register?",
    ar: "هل أحتاج إلى شهادة أيزو للتسجيل؟",
  },
  a: {
    en: "Check the buyer's current requirements for your activity and category. A request for quality or HSE management evidence does not by itself establish a requirement for every ISO certificate. Where a certificate is requested, check its standard, scope, validity and any accreditation condition before commissioning work.",
    ar: "تحقّق من متطلبات المشتري الحالية لنشاطك وفئتك. فطلب أدلة على إدارة الجودة أو الصحة والسلامة والبيئة لا يعني بحد ذاته اشتراط جميع شهادات الأيزو. وعند طلب شهادة، راجع المواصفة والنطاق والصلاحية وأي شرط للاعتماد قبل الاتفاق على العمل.",
  },
};

export const vendors: VendorOperator[] = [
  {
    slug: "basra-oil-company",
    image: getPageImage("/services/oil-gas-registration/basra-oil-company", "editorial"),
    keyPoints: [
      {
        en: "Confirm whether the application is for an Iraqi or a foreign company.",
        ar: "أكّد ما إذا كان الطلب لشركة عراقية أم أجنبية.",
      },
      {
        en: "Use the document list that applies to your company and activity.",
        ar: "استخدم قائمة المستمسكات المنطبقة على شركتك ونشاطها.",
      },
      {
        en: "Registration, tender eligibility and contract awards are separate decisions.",
        ar: "التسجيل وأهلية المناقصة وإحالة العقود قرارات منفصلة.",
      },
    ],

    name: { en: "Basra Oil Company (BOC)", ar: "شركة نفط البصرة" },
    metaTitle: {
      en: "Register as a Basra Oil Company Vendor (2026) | IraqCert",
      ar: "تسجيل الموردين في شركة نفط البصرة: دليل 2026 | عراق سيرت",
    },
    title: {
      en: "How to register as a vendor with Basra Oil Company (BOC)",
      ar: "كيف تسجّل شركتك كمورد معتمد لدى شركة نفط البصرة",
    },
    description: {
      en: "BOC vendor registration: the published Iraqi-company document list, its capital requirement, applicant forms and re-evaluation guidance.",
      ar: "تسجيل الموردين لدى شركة نفط البصرة: قائمة المستمسكات المنشورة للشركات العراقية ومتطلب رأس المال واستمارات المتقدمين وإرشادات إعادة التقييم.",
    },
    intro: {
      en: "A registration file from another supplier can look like a useful shortcut. For a BOC application, its value depends on who applied and what that company delivers. Start with your own legal entity, the work you want to supply and the evidence you can provide. Then identify the form and document requirements that fit that application.",
      ar: "قد يبدو ملف تسجيل مورّد آخر طريقاً مختصراً مفيداً. لكن فائدته لطلب التسجيل لدى شركة نفط البصرة تعتمد على هوية الشركة التي تقدّمت وطبيعة أعمالها. ابدأ بالكيان القانوني لشركتك والأعمال التي تريد تقديمها والأدلة المتاحة لديك، ثم حدّد الاستمارة ومتطلبات المستمسكات المنطبقة على هذا الطلب.",
    },
    facts: [
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Where to apply", ar: "جهة التقديم" },
        value: {
          en: "Confirm the current submission arrangements with BOC. Its published Iraqi-company list requests documents and a CD containing them.",
          ar: "أكّد ترتيبات التقديم الحالية مع شركة نفط البصرة. وتطلب قائمتها المنشورة للشركات العراقية مستمسكات وقرصاً مدمجاً يحتوي عليها.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Minimum capital", ar: "الحد الأدنى لرأس المال" },
        value: {
          en: "For the published Iraqi-company list: at least 2,000,000,000 IQD in the certificate of establishment, certified by the Companies Registration Department.",
          ar: "وفق القائمة المنشورة للشركات العراقية: رأس مال لا يقل عن 2,000,000,000 دينار عراقي في شهادة التأسيس المصدّقة من دائرة تسجيل الشركات.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Financial history", ar: "السجل المالي" },
        value: {
          en: "Published Iraqi-company list: certified final accounts for the last 3 years.",
          ar: "القائمة المنشورة للشركات العراقية: الحسابات الختامية لآخر 3 سنوات مصدّقة من جهة قانونية.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Tax clearance", ar: "براءة الذمة الضريبية" },
        value: {
          en: "Published Iraqi-company list: General Tax Authority clearance for the last 3 years.",
          ar: "القائمة المنشورة للشركات العراقية: براءة ذمة من الهيئة العامة للضرائب لآخر 3 سنوات.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Classification", ar: "التصنيف" },
        value: {
          en: "Published Iraqi-company list: classification grade and type. Confirm the applicable classification evidence with BOC.",
          ar: "القائمة المنشورة للشركات العراقية: درجة التصنيف ونوعها. أكّد مع شركة نفط البصرة مستند التصنيف المنطبق على نشاطك.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=32",
        label: { en: "Forms", ar: "الاستمارات" },
        value: {
          en: "Separate information forms for Iraqi companies and for foreign or Arab companies, both refreshed 8 July 2025.",
          ar: "استمارتا معلومات منفصلتان للشركات العراقية وللشركات الأجنبية والعربية، حُدّثتا في 8 تموز 2025.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Re-evaluation", ar: "إعادة التقييم" },
        value: {
          en: "Every 2 years, based on your participation in BOC tenders in your specialization.",
          ar: "كل سنتين، بناءً على مشاركة الشركة في مناقصات شركة نفط البصرة ضمن اختصاصها.",
        },
      },
      {
        sourceUrl: "https://boc.oil.gov.iq/?page=9",
        label: { en: "Registration fee", ar: "رسوم التسجيل" },
        value: {
          en: "The reviewed document list does not state a fee. This does not establish that all registration steps are free; confirm current charges with BOC.",
          ar: "لا تحدّد قائمة المستمسكات التي راجعناها رسوماً. ولا يثبت ذلك أن جميع خطوات التسجيل مجانية؛ أكّد أي رسوم حالية مع شركة نفط البصرة.",
        },
      },
    ],
    sections: [
      {
        heading: {
          en: "Connect the application to the work you can deliver",
          ar: "اربط الطلب بالأعمال التي تستطيع تنفيذها",
        },
        paragraphs: [
          {
            en: "A pump contractor may repair equipment in its workshop, install it on site or supply replacement parts. Those activities need a clear description, backed by the relevant team, equipment and completed work. A general company profile leaves the reviewer to work out which part of the offer is supported.",
            ar: "قد يصلح مقاول المضخات المعدات في ورشته، أو يركّبها في الموقع، أو يورّد قطع الغيار. تحتاج هذه الأنشطة إلى وصف واضح تدعمه معلومات عن الفريق والمعدات والأعمال المنجزة ذات الصلة. أما الملف العام للشركة فيترك للمراجع مهمة تحديد الأنشطة التي تدعمها الأدلة.",
          },
          {
            en: "Compare that offer with the buyer’s requirements before commissioning registration support. IraqCert helps organise the application and follow-up within an agreed scope; BOC retains the registration decision. A listing does not establish eligibility for every tender or promise a contract.",
            ar: "قارن ما تقدّمه بمتطلبات الجهة المشترية قبل الاتفاق على دعم التسجيل. تساعد IraqCert على تنظيم الطلب ومتابعته وفق نطاق متفق عليه، وتبقى قرارات التسجيل لشركة نفط البصرة. ولا يعني الإدراج في القائمة استيفاء شروط كل مناقصة أو ضمان عقد.",
          },
        ],
      },
      {
        heading: {
          en: "Match the form to the applicant",
          ar: "طابق الاستمارة مع صفة المتقدّم",
        },
        paragraphs: [
          {
            en: "BOC’s published information distinguishes Iraqi companies from foreign and Arab companies. The eleven-item document list is expressly for the assessment of Iraqi companies. A separate foreign-company form is a reason to check the route carefully, rather than copy every item from an Iraqi supplier’s file.",
            ar: "تميّز المعلومات المنشورة لدى شركة نفط البصرة بين الشركات العراقية والشركات الأجنبية والعربية. وقائمة المستمسكات ذات البنود الأحد عشر مخصصة صراحةً لدراسة الشركات العراقية. ووجود استمارة منفصلة للشركات الأجنبية يستدعي مراجعة المسار بعناية، بدلاً من نسخ جميع بنود ملف مورّد عراقي.",
          },
          {
            en: "Confirm the applicant’s legal name, country of incorporation and intended activity, then ask BOC which form and supporting documents apply. Confirm the current submission channel and delivery arrangements at the same time. The published list requests a CD containing the documents; it does not establish every current submission option.",
            ar: "حدّد الاسم القانوني للمتقدّم وبلد التأسيس والنشاط المقصود، ثم اسأل شركة نفط البصرة عن الاستمارة والمستمسكات المنطبقة. وأكّد في الوقت نفسه قناة التقديم وترتيبات التسليم الحالية. تطلب القائمة المنشورة قرصاً مدمجاً يحتوي على المستمسكات، لكنها لا تحدّد جميع خيارات التقديم الحالية.",
          },
        ],
      },
      {
        heading: {
          en: "Build the file from evidence the company holds",
          ar: "ابنِ الملف على الأدلة التي تحتفظ بها الشركة",
        },
        paragraphs: [
          {
            en: "For the published Iraqi-company route, the list includes certified final accounts for the last three years, similar work and contracts, tax clearance, equipment and staff information. These records need to tell a consistent story about the same applicant. Identify missing or outdated items before assembling the final file.",
            ar: "يتضمن المسار المنشور للشركات العراقية الحسابات الختامية المصدّقة لآخر ثلاث سنوات، والأعمال والعقود المماثلة، وبراءة الذمة الضريبية، ومعلومات المعدات والموظفين. ينبغي أن تقدّم هذه السجلات معلومات متسقة عن المتقدّم نفسه. حدّد البنود الناقصة أو القديمة قبل تجميع الملف النهائي.",
          },
          {
            en: "The list also names classification grade and type, and a certificate of establishment showing at least 2 billion IQD in capital. Confirm the classification evidence appropriate to the company’s activity. These are stated requirements of the Iraqi-company list, not a universal checklist for every foreign applicant.",
            ar: "تذكر القائمة أيضاً درجة التصنيف ونوعها، وشهادة تأسيس برأس مال لا يقل عن ملياري دينار عراقي. أكّد مستند التصنيف المناسب لنشاط الشركة. وهذه متطلبات واردة في قائمة الشركات العراقية، وليست قائمة موحّدة لكل متقدّم أجنبي.",
          },
        ],
      },
      {
        heading: {
          en: "Confirm HSE and ISO evidence",
          ar: "تأكيد متطلبات الصحة والسلامة والبيئة وشهادات الأيزو",
        },
        paragraphs: [
          {
            en: "The reviewed eleven-item Iraqi-company list does not establish a blanket ISO or accreditation requirement. Check any separate HSE or certification instruction for your activity or intended tender with BOC. If it names a standard, issuer or accreditation condition, use that wording to define the certification work before requesting an offer.",
            ar: "لا تثبت قائمة الشركات العراقية ذات البنود الأحد عشر التي راجعناها شرطاً عاماً لشهادة أيزو أو لاعتماد محدد. راجع مع شركة نفط البصرة أي تعليمات مستقلة للصحة والسلامة والبيئة أو للشهادات تخص نشاطك أو المناقصة المستهدفة. وإذا سمّت مواصفة أو جهة مانحة أو شرط اعتماد، فاستخدم هذا النص لتحديد أعمال الشهادة قبل طلب عرض سعر.",
          },
        ],
      },
      {
        heading: {
          en: "Foreign companies: resolve the entity question first",
          ar: "الشركات الأجنبية: احسم صفة الكيان المتقدّم أولاً",
        },
        paragraphs: [
          {
            en: "A foreign-company form alone does not settle whether your proposed activity requires an Iraqi branch or company. Ask BOC which entity should apply and which evidence it needs. Any separate incorporation work should follow the confirmed requirement, with its own agreed scope.",
            ar: "لا تحسم استمارة الشركة الأجنبية وحدها ما إذا كان النشاط المقترح يتطلب فرعاً أو شركة عراقية. اسأل شركة نفط البصرة عن الكيان الذي ينبغي أن يتقدّم والأدلة المطلوبة منه. وأي أعمال منفصلة لتأسيس كيان ينبغي أن تستند إلى المتطلب المؤكّد وبنطاق عمل مستقل متفق عليه.",
          },
        ],
      },
      {
        heading: {
          en: "After listing, keep the tender conditions in view",
          ar: "بعد التسجيل، راجع شروط كل مناقصة",
        },
        paragraphs: [
          {
            en: "BOC’s published guidance states that it re-evaluates companies every two years in relation to participation in tenders within their specialisation. A company that does not participate may have its registration reconsidered. Keep the company file current and review each tender’s conditions separately.",
            ar: "تذكر إرشادات شركة نفط البصرة المنشورة إعادة تقييم الشركات كل سنتين بالنظر إلى مشاركتها في المناقصات ضمن اختصاصها. وقد يُعاد النظر في تسجيل الشركة التي لا تشارك. حافظ على تحديث ملف الشركة وراجع شروط كل مناقصة على حدة.",
          },
        ],
      },
      {
        heading: {
          en: "Other buyers have their own registration routes",
          ar: "للجهات المشترية الأخرى مسارات تسجيل خاصة بها",
        },
        paragraphs: [
          {
            en: "If your target is Basrah Gas Company or Rumaila, use that buyer’s instructions. A BOC registration file does not replace another operator’s application. Identify the named buyer and the work you want to supply before deciding which registration service you need.",
            ar: "إذا كانت الجهة المستهدفة شركة غاز البصرة أو الرميلة، فاعتمد تعليماتها الخاصة. لا يحل ملف التسجيل لدى شركة نفط البصرة محل طلب شركة مشغّلة أخرى. حدّد الجهة المشترية بالاسم والأعمال التي تريد تقديمها قبل اختيار خدمة التسجيل المناسبة.",
          },
        ],
        links: [
          {
            label: {
              en: "Basrah Gas Company registration",
              ar: "التسجيل لدى شركة غاز البصرة",
            },
            to: "/services/oil-gas-registration/basrah-gas-company",
          },
          {
            label: {
              en: "Rumaila registration through Avetta",
              ar: "التسجيل في الرميلة عبر Avetta",
            },
            to: "/services/oil-gas-registration/rumaila-avetta",
          },
          {
            label: {
              en: "Oil and gas vendor registration",
              ar: "تسجيل موردي النفط والغاز",
            },
            to: "/services/ministry-of-oil-vendor-registration",
          },
        ],
      },
    ],
    steps: [
      {
        title: { en: "Eligibility check", ar: "فحص الأهلية" },
        text: {
          en: "We identify your applicant entity and compare the available evidence with the applicable BOC instructions. The published Iraqi-company checklist is used within its stated scope.",
          ar: "نحدّد الكيان المتقدّم ونقارن الأدلة المتاحة بتعليمات شركة نفط البصرة المنطبقة عليه. وتُستخدم قائمة الشركات العراقية المنشورة ضمن نطاقها المحدّد.",
        },
      },
      {
        title: { en: "File assembly", ar: "إعداد الملف" },
        text: {
          en: "We organise the documents required for the confirmed applicant route, complete the applicable form and prepare the requested digital copy. Additional HSE or ISO material is included only where the applicable requirement is established.",
          ar: "ننظّم مستمسكات المسار المؤكّد للمتقدّم، ونستكمل الاستمارة المنطبقة، ونُعدّ النسخة الرقمية المطلوبة. ولا تُدرج مستندات إضافية للصحة والسلامة والبيئة أو للأيزو إلا بعد تأكيد المتطلب المنطبق.",
        },
      },
      {
        title: { en: "Submission coordination and follow-up", ar: "تنسيق التقديم والمتابعة" },
        text: {
          en: "We agree the submission and follow-up scope, track requests for clarification and record BOC’s response. Listing and evaluation decisions remain with BOC; support cannot guarantee approval.",
          ar: "نتفق على نطاق التقديم والمتابعة، ونتابع طلبات التوضيح ونسجّل رد شركة نفط البصرة. وتبقى قرارات الإدراج والتقييم للشركة؛ ولا يضمن الدعم الموافقة.",
        },
      },
    ],
    documentsTitle: {
      en: "BOC’s published document list for Iraqi companies",
      ar: "قائمة المستمسكات المنشورة لشركة نفط البصرة للشركات العراقية",
    },
    documents: [
      {
        en: "Updated original certificate of establishment showing capital of at least 2 billion IQD, certified by the Ministry of Trade Companies Registration Department",
        ar: "شهادة التأسيس الأصلية المحدّثة برأس مال لا يقل عن 2 مليار دينار عراقي، مصدّقة من دائرة تسجيل الشركات في وزارة التجارة",
      },
      {
        en: "Final accounts for the last 3 years, certified by a legal authority",
        ar: "الحسابات الختامية لآخر 3 سنوات مصدّقة من جهة قانونية",
      },
      {
        en: "Classification grade and type",
        ar: "درجة التصنيف ونوعها",
      },
      {
        en: "Company file: field of specialization and similar works and contracts executed in the last 3 years",
        ar: "ملف الشركة: مجال الاختصاص والأعمال والعقود المماثلة المنفّذة خلال آخر 3 سنوات",
      },
      {
        en: "Good-performance certificates from previous clients",
        ar: "شهادات حسن أداء من جهات تعاملت معها الشركة سابقاً",
      },
      {
        en: "Official authorizations held by the company",
        ar: "التخويلات الرسمية التي تحملها الشركة",
      },
      {
        en: "Tax clearance letter from the General Tax Authority for the last 3 years",
        ar: "كتاب براءة ذمة من الهيئة العامة للضرائب لآخر 3 سنوات",
      },
      {
        en: "List of company-owned machinery and equipment",
        ar: "قائمة المكائن والمعدات المملوكة للشركة",
      },
      { en: "Staff list with CVs", ar: "قائمة الملاك الوظيفي مع السير الذاتية" },
      { en: "Company website", ar: "الموقع الإلكتروني للشركة" },
      {
        en: "A CD containing all of the listed material",
        ar: "قرص مدمج يحتوي على جميع المستمسكات المدرجة",
      },
    ],
    isoNeeded: [],
    sources: [
      {
        label: {
          en: "BOC: documents required for the study of Iraqi companies",
          ar: "شركة نفط البصرة: المستمسكات المطلوبة لغرض دراسة الشركات العراقية",
        },
        url: "https://boc.oil.gov.iq/?page=9",
      },
      {
        label: {
          en: "BOC: supplier and contractor information form (8 July 2025)",
          ar: "شركة نفط البصرة: نموذج المعلومات لتقديم الموردين والمقاولين لأغراض التسجيل والاعتماد (8 تموز 2025)",
        },
        url: "https://boc.oil.gov.iq/?page=31",
      },
      {
        label: {
          en: "BOC: information forms for Iraqi and foreign companies",
          ar: "شركة نفط البصرة: استمارات المعلومات الخاصة بالشركات العراقية والأجنبية",
        },
        url: "https://boc.oil.gov.iq/?page=32",
      },
      {
        label: {
          en: "Ministry of Planning: contractor registration and classification",
          ar: "وزارة التخطيط: تسجيل وتصنيف شركات المقاولات والمقاولين",
        },
        url: "https://mop.gov.iq/contractors",
      },
      {
        label: {
          en: "Basrah Gas Company: doing business with BGC",
          ar: "شركة غاز البصرة: التعامل مع الشركة",
        },
        url: "https://www.basrahgas.com/doing-business-with-bgc/",
      },
      {
        label: {
          en: "Rumaila: doing business with us (Avetta)",
          ar: "الرميلة: التعامل معنا (Avetta)",
        },
        url: "https://rumaila.iq/english/serving-iraq/doing-business-with-us/",
      },
      {
        label: {
          en: "Iraq Business News: new digital company registration platform (27 April 2026)",
          ar: "Iraq Business News: منصة رقمية جديدة لتسجيل الشركات (27 نيسان 2026)",
        },
        url: "https://www.iraq-businessnews.com/2026/04/27/new-digital-platform-for-company-registration-in-iraq/",
      },
    ],
    faq: [
      {
        q: {
          en: "How do I register as a vendor with Basra Oil Company?",
          ar: "كيف أسجّل كمورد لدى شركة نفط البصرة؟",
        },
        a: {
          en: "Confirm your applicant entity and the current form with BOC. Its published Iraqi-company checklist lists eleven items, including capital of at least 2 billion IQD, three years of certified accounts, classification grade and type, and tax clearance. Confirm submission arrangements and any requirements specific to your activity.",
          ar: "أكّد صفة كيانك المتقدّم والاستمارة الحالية مع شركة نفط البصرة. وتضم قائمتها المنشورة للشركات العراقية أحد عشر بنداً، منها رأس مال لا يقل عن ملياري دينار، وحسابات ختامية مصدّقة لثلاث سنوات، ودرجة التصنيف ونوعها، وبراءة الذمة الضريبية. أكّد ترتيبات التقديم وأي متطلبات تخص نشاطك.",
        },
      },
      {
        q: {
          en: "Is there an online portal for Basra Oil Company vendor registration?",
          ar: "هل توجد منصة إلكترونية لتسجيل الموردين في شركة نفط البصرة؟",
        },
        a: {
          en: "The reviewed BOC pages publish forms and a document list; they do not establish all current submission options. Confirm the current submission channel directly with BOC. Rumaila and Basrah Gas Company have separate procedures.",
          ar: "تنشر صفحات شركة نفط البصرة التي راجعناها استمارات وقائمة مستمسكات، لكنها لا تثبت جميع خيارات التقديم الحالية. أكّد قناة التقديم الحالية مباشرةً مع الشركة. وللرميلة وشركة غاز البصرة إجراءات منفصلة.",
        },
      },
      {
        q: {
          en: "What is the minimum capital to register with BOC?",
          ar: "ما الحد الأدنى لرأس المال للتسجيل لدى شركة نفط البصرة؟",
        },
        a: {
          en: "The published list for Iraqi companies states at least 2 billion Iraqi dinars on the certificate of establishment, certified by the Companies Registration Department. Confirm the requirements for your legal entity; do not extend this figure to every foreign applicant without confirmation.",
          ar: "تنص القائمة المنشورة للشركات العراقية على رأس مال لا يقل عن ملياري دينار عراقي في شهادة التأسيس المصدّقة من دائرة تسجيل الشركات. أكّد متطلبات كيانك القانوني، ولا تعمّم هذا المبلغ على جميع المتقدمين الأجانب دون تأكيد.",
        },
      },
      {
        q: {
          en: "How long does Basra Oil Company registration stay valid?",
          ar: "كم تبقى صلاحية الاعتماد لدى شركة نفط البصرة؟",
        },
        a: {
          en: "BOC re-evaluates registered companies every two years based on their participation in its tenders. A listed company that does not bid in its specialization can have its registration reconsidered.",
          ar: "تُعيد شركة نفط البصرة تقييم الشركات المسجّلة كل سنتين بناءً على مشاركتها في مناقصاتها. والشركة المعتمدة التي لا تشارك ضمن اختصاصها يمكن أن يُعاد النظر في تسجيلها.",
        },
      },
      {
        q: {
          en: "Does BOC’s published Iraqi-company list require ISO certification?",
          ar: "هل تشترط قائمة الشركات العراقية المنشورة شهادة أيزو؟",
        },
        a: {
          en: "The reviewed eleven-item list does not establish a blanket ISO or accreditation requirement. Check any separate instruction for your activity or tender with BOC. Where a requirement is explicit, confirm the accepted standard, scope and issuing route before arranging certification.",
          ar: "لا تثبت القائمة ذات البنود الأحد عشر التي راجعناها شرطاً عاماً للأيزو أو للاعتماد. أكّد مع شركة نفط البصرة أي تعليمات منفصلة تخص نشاطك أو المناقصة. وعند وجود متطلب صريح، تحقّق من المواصفة والنطاق ومسار الإصدار المقبول قبل ترتيب خدمة الشهادة.",
        },
      },
      {
        q: {
          en: "Does Basra Oil Company charge a registration fee?",
          ar: "هل تفرض شركة نفط البصرة رسوماً على التسجيل؟",
        },
        a: {
          en: "The reviewed document list does not state a registration fee. That omission is not confirmation that all steps are free. Check current official charges with BOC. IraqCert’s agreed professional preparation and follow-up fees are separate and do not purchase listing or approval.",
          ar: "لا تحدّد قائمة المستمسكات التي راجعناها رسوماً للتسجيل. ولا يعني عدم ذكر الرسوم أن جميع الخطوات مجانية. تحقّق من الرسوم الرسمية الحالية مع شركة نفط البصرة. وتكون أتعاب عراق سيرت المهنية المتفق عليها للإعداد والمتابعة منفصلة، ولا تضمن الإدراج أو الموافقة.",
        },
      },
      {
        q: {
          en: "Can a foreign company register with BOC without an Iraqi entity?",
          ar: "هل يمكن لشركة أجنبية التسجيل لدى شركة نفط البصرة دون كيان عراقي؟",
        },
        a: {
          en: "Confirm this directly with BOC for your proposed applicant and activity. The published Iraqi-company checklist does not establish the legal-entity requirements for every foreign applicant, and a separate foreign-company form alone does not resolve those requirements.",
          ar: "أكّد ذلك مباشرةً مع شركة نفط البصرة وفق الكيان المتقدّم والنشاط المقترح. لا تثبت قائمة الشركات العراقية المنشورة متطلبات الكيان القانوني لجميع المتقدمين الأجانب، ولا تحسم استمارة منفصلة للشركات الأجنبية وحدها تلك المتطلبات.",
        },
      },
      {
        q: {
          en: "Where is Basra Oil Company based?",
          ar: "أين تقع شركة نفط البصرة؟",
        },
        a: {
          en: "Basra Oil Company is based in Basra, southern Iraq. Confirm the relevant department and submission arrangements directly with the company before delivering a file.",
          ar: "يقع مقر شركة نفط البصرة في البصرة، جنوب العراق. أكّد القسم المختص وترتيبات التقديم مباشرةً مع الشركة قبل تسليم الملف.",
        },
      },
    ],
  },
  {
    slug: "rumaila-avetta",
    name: { en: "Rumaila (ROO)", ar: "حقل الرميلة (ROO)" },
    metaTitle: {
      en: "Rumaila (ROO) Supplier Registration via Avetta | IraqCert",
      ar: "تسجيل موردي الرميلة عبر Avetta | عراق سيرت",
    },
    title: {
      en: "Rumaila (ROO) Supplier Registration via Avetta",
      ar: "تسجيل موردي حقل الرميلة (ROO) عبر Avetta",
    },
    description: {
      en: "Rumaila supplier registration through Avetta: company documents, work types, HSE evidence and annual subscription. IraqCert helps prepare your dossier.",
      ar: "تسجيل موردي الرميلة عبر Avetta: وثائق الشركة وأنواع الأعمال وأدلة السلامة والاشتراك السنوي. تساعدك عراق سيرت في إعداد ملف التسجيل ومراجعة نواقصه.",
    },
    intro: {
      en: "Suppliers interested in working with Rumaila Operating Organisation (ROO) register through its Avetta supplier system. IraqCert helps Iraqi companies prepare the company profile and supporting dossier, review missing evidence and respond to document queries. Registration and document support do not guarantee qualification, a tender invitation or a contract.",
      ar: "يسجّل الموردون الراغبون في العمل مع منظمة تشغيل الرميلة (ROO) عبر نظامها للموردين على منصة Avetta. تساعد عراق سيرت الشركات العراقية في إعداد بيانات الشركة والملف الداعم ومراجعة الأدلة الناقصة والرد على الاستفسارات المتعلقة بالوثائق. ولا يضمن التسجيل أو دعم إعداد الملف التأهيل أو دعوة إلى مناقصة أو عقداً.",
    },
    facts: [
      {
        label: { en: "Platform fee", ar: "رسوم المنصة" },
        value: {
          en: "Avetta has an annual subscription. Confirm the membership charge for your service types directly with Avetta; it is separate from IraqCert's agreed assistance fee.",
          ar: "تفرض Avetta اشتراكاً سنوياً. تحقّق من قيمة العضوية بحسب أنواع خدماتك مباشرةً لدى المنصة؛ وهي منفصلة عن أتعاب المساعدة المتفق عليها مع عراق سيرت.",
        },
      },
      {
        label: { en: "Tender and award decisions", ar: "قرارات المناقصات والعقود" },
        value: {
          en: "ROO retains discretion over tender invitations and contract awards. An Avetta account is not an award or a promise of work.",
          ar: "تحتفظ منظمة تشغيل الرميلة بصلاحية اتخاذ قرارات الدعوة إلى المناقصات وإسناد العقود. ولا يُعد حساب Avetta إحالة عقد أو وعداً بالعمل.",
        },
      },
    ],
    sources: [
      {
        label: {
          en: "Rumaila: doing business with us (Avetta)",
          ar: "الرميلة: التعامل معنا (Avetta)",
        },
        url: "https://rumaila.iq/english/serving-iraq/doing-business-with-us/",
      },
      {
        label: {
          en: "ROO's Avetta supplier registration guide",
          ar: "دليل منظمة تشغيل الرميلة لتسجيل الموردين في Avetta",
        },
        url: "https://rumaila.iq/wp-content/uploads/2025/06/Supplier-Registration-Steps-English.pdf",
      },
    ],
    sections: [
      {
        heading: {
          en: "Which Avetta client connection should an Iraqi company use?",
          ar: "أي ارتباط بالعميل تختاره الشركة العراقية في Avetta؟",
        },
        paragraphs: [
          {
            en: "ROO's published guide distinguishes BECL Potential Iraq for an Iraq-based company from BECL Potential Overseas for a company based abroad. It says to select only the applicable one. Check the current guide and any invitation before connecting an account; selecting extra client sites can change the documents the system asks for.",
            ar: "يميّز دليل منظمة تشغيل الرميلة المنشور بين BECL Potential Iraq للشركة الموجودة في العراق وBECL Potential Overseas للشركة الموجودة خارجه، ويطلب اختيار الارتباط المناسب فقط. راجع الدليل الحالي وأي دعوة قبل ربط الحساب؛ فقد يؤثر اختيار مواقع عملاء إضافية في الوثائق التي يطلبها النظام.",
          },
        ],
      },
    ],
    platform: {
      name: "Avetta",
      note: {
        en: "Use the Avetta link on ROO's official supplier page. Prepare the information requested for your company, work types and client connection, then address the tasks shown in your account.",
        ar: "استخدم رابط Avetta الموجود في صفحة الموردين الرسمية لمنظمة تشغيل الرميلة. جهّز المعلومات المطلوبة لشركتك وأنواع أعمالها وارتباطها بالعميل، ثم عالج المهام الظاهرة في حسابك.",
      },
    },
    steps: [
      {
        title: { en: "Company and work-type review", ar: "مراجعة الشركة وأنواع الأعمال" },
        text: {
          en: "We review the legal entity, location, activities and existing account or invitation before preparing the profile.",
          ar: "نراجع الكيان القانوني والموقع والأنشطة والحساب الحالي أو الدعوة قبل إعداد بيانات الشركة.",
        },
      },
      {
        title: { en: "Dossier preparation", ar: "إعداد ملف التسجيل" },
        text: {
          en: "We help organise registration and HSE evidence and review the questionnaires. Insurance or ISO evidence is checked against the actual request.",
          ar: "نساعد في تنظيم وثائق التسجيل وأدلة الصحة والسلامة والبيئة ومراجعة الاستبيانات. وتُراجع وثائق التأمين أو الأيزو وفق الطلب الفعلي.",
        },
      },
      {
        title: { en: "Queries and updates", ar: "الاستفسارات والتحديثات" },
        text: {
          en: "We help address document queries and keep the agreed dossier current. Qualification and procurement decisions remain with the relevant reviewers and buyer.",
          ar: "نساعد في معالجة استفسارات الوثائق وتحديث الملف ضمن نطاق العمل المتفق عليه. وتبقى قرارات التأهيل والشراء لدى جهات المراجعة والمشتري.",
        },
      },
    ],
    documentsTitle: {
      en: "Information ROO asks suppliers to prepare",
      ar: "المعلومات التي تطلب الرميلة تجهيزها",
    },
    documents: [
      { en: "Company profile", ar: "نبذة الشركة" },
      { en: "Types of work offered", ar: "أنواع الأعمال المقدّمة" },
      {
        en: "Supporting health, safety and environment documents",
        ar: "الوثائق الداعمة للصحة والسلامة والبيئة",
      },
      { en: "Company registration documents", ar: "وثائق تسجيل الشركة" },
      { en: "Acknowledgements of the applicable policies", ar: "الإقرارات بالسياسات المعمول بها" },
    ],
    isoNeeded: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 29001"],
    isoNote: {
      en: "ROO's public starting list is not a blanket ISO certificate requirement. Review the tasks and work-type requirements in your Avetta account before deciding which certificates or other management-system evidence are needed.",
      ar: "لا تفرض قائمة الرميلة العامة الأولية جميع شهادات الأيزو على كل مورد. راجع مهام حسابك في Avetta ومتطلبات نوع العمل قبل تحديد الشهادات أو أدلة نظام الإدارة الأخرى المطلوبة.",
    },
    faq: [
      {
        q: {
          en: "How do I register as a supplier for Rumaila (ROO)?",
          ar: "كيف أسجّل كمورد لحقل الرميلة (ROO)؟",
        },
        a: {
          en: "Start from the Avetta link on ROO's official supplier page and follow its registration guide for your company location. IraqCert can help prepare the profile, supporting documents and responses to account tasks.",
          ar: "ابدأ من رابط Avetta في صفحة الموردين الرسمية للرميلة واتبع دليل التسجيل بحسب موقع شركتك. ويمكن لعراق سيرت المساعدة في إعداد بيانات الشركة والوثائق الداعمة والردود على مهام الحساب.",
        },
      },
      {
        q: { en: "What is Avetta?", ar: "ما هي منصة Avetta؟" },
        a: {
          en: "Avetta is a supplier and contractor pre-qualification platform used across oil and gas. Operators like Rumaila use it to verify supplier compliance before awarding work.",
          ar: "Avetta منصة للتأهيل المسبق للموردين والمقاولين تُستخدم في قطاع النفط والغاز. وتستخدمها جهات مُشغِّلة مثل الرميلة للتحقق من امتثال الموردين قبل إسناد الأعمال.",
        },
      },
      {
        q: {
          en: "Does Avetta registration guarantee a Rumaila contract?",
          ar: "هل يضمن التسجيل في Avetta عقداً مع الرميلة؟",
        },
        a: {
          en: "No. Registration does not guarantee tender invitations or contract awards. ROO makes those decisions according to its business requirements.",
          ar: "لا. لا يضمن التسجيل الدعوة إلى مناقصة أو إسناد عقد. وتتخذ منظمة تشغيل الرميلة هذه القرارات وفق احتياجات أعمالها.",
        },
      },
      {
        q: {
          en: "Is Rumaila's Avetta registration free?",
          ar: "هل التسجيل في Avetta للرميلة مجاني؟",
        },
        a: {
          en: "Avetta charges an annual subscription. Check the applicable charge with Avetta; any separately agreed IraqCert document assistance has its own scope and fee.",
          ar: "تفرض Avetta اشتراكاً سنوياً. تحقّق من المبلغ المطبّق لدى المنصة؛ ولأي مساعدة تتفق عليها بصورة منفصلة مع عراق سيرت في إعداد الوثائق نطاقها وأتعابها الخاصة.",
        },
      },
      isoFaq,
    ],
  },
  {
    slug: "basrah-gas-company",
    name: { en: "Basrah Gas Company (BGC)", ar: "شركة غاز البصرة" },
    metaTitle: {
      en: "Basrah Gas Company Vendor Registration 2026 | IraqCert",
      ar: "تسجيل الموردين في شركة غاز البصرة 2026 | عراق سيرت",
    },
    title: {
      en: "Basrah Gas Company (BGC) vendor registration",
      ar: "تسجيل الموردين والمقاولين في شركة غاز البصرة (BGC)",
    },
    description: {
      en: "Prepare for Basrah Gas Company vendor registration: official contacts, documents to organise and application support. Ask IraqCert to review your company file.",
      ar: "استعد لتسجيل شركتك لدى شركة غاز البصرة: جهات الاتصال الرسمية، وتجهيز المستمسكات، ودعم طلبك. تواصل مع عراق سيرت لمراجعة ملف شركتك وخطوات التقديم.",
    },
    intro: {
      en: "Start BGC vendor registration with its vendor-management team. BGC reviews company capabilities for categorisation and possible shortlisting; registration does not guarantee a tender invitation or contract. IraqCert helps you prepare a focused company file, clarify the submission requirements and manage follow-up on your behalf.",
      ar: "ابدأ تسجيل الموردين لدى شركة غاز البصرة بالتواصل مع فريق إدارة الموردين. تراجع الشركة قدرات المورد لتصنيفه والنظر في إدراجه ضمن القوائم المختصرة؛ ولا يضمن التسجيل دعوة إلى مناقصة أو عقداً. تساعدك عراق سيرت في تجهيز ملف واضح، وتحديد متطلبات التقديم، ومتابعة الطلب نيابةً عنك.",
    },
    areaServed: [{ type: "Country", name: "Iraq" }],
    facts: [
      {
        label: { en: "Registration contact instructions", ar: "تعليمات التواصل للتسجيل" },
        value: {
          en: "Use the Vendor Registration section on BGC's official supplier page. Its registration and FAQ sections publish different mailboxes; confirm the active channel with BGC before sending company documents.",
          ar: "استخدم قسم تسجيل الموردين في صفحة الشركة الرسمية. يعرض قسما التسجيل والأسئلة الشائعة عنوانَي بريد مختلفين؛ أكّد قناة التواصل المعتمدة مع شركة غاز البصرة قبل إرسال وثائق الشركة.",
        },
      },
      {
        label: { en: "Official consideration", ar: "مقابل النظر في الطلب" },
        value: {
          en: "BGC prohibits its staff and agents from charging suppliers for consideration for registration, tenders or awards.",
          ar: "تمنع شركة غاز البصرة موظفيها ووكلاءها من طلب مبالغ مقابل النظر في التسجيل أو المناقصات أو منح العقود.",
        },
      },
      {
        label: { en: "Supplier page checked", ar: "تاريخ التحقق من صفحة الموردين" },
        value: { en: "7 October 2026", ar: "7 تشرين الأول 2026" },
      },
    ],
    sections: [
      {
        heading: {
          en: "Where is the BGC registration portal?",
          ar: "أين رابط منصة تسجيل موردي شركة غاز البصرة؟",
        },
        paragraphs: [
          {
            en: "BGC's public supplier page mentions online registration but does not link a portal. Use its Vendor Registration section, linked under Sources below, to confirm the current submission channel and instructions with BGC before sending company documents.",
            ar: "تذكر صفحة الموردين العامة للشركة التسجيل الإلكتروني، لكنها لا تعرض رابطاً للمنصة. استخدم قسم تسجيل الموردين فيها، الوارد رابطه ضمن المصادر أدناه، لتأكيد قناة التقديم وتعليماتها الحالية مع شركة غاز البصرة قبل إرسال وثائق الشركة.",
          },
        ],
      },
      {
        heading: {
          en: "Prepare evidence that explains what your company can deliver",
          ar: "جهّز أدلة توضّح ما تستطيع شركتك تقديمه",
        },
        paragraphs: [
          {
            en: "Use the checklist below to organise a review file for IraqCert. It is preparation guidance: BGC's public registration page does not publish a complete attachment checklist or name a mandatory ISO management-system certificate. Confirm the requirements for your activity with BGC.",
            ar: "استخدم القائمة أدناه لتنظيم ملف المراجعة لدى عراق سيرت. وهي إرشادات للتحضير؛ فصفحة التسجيل العامة لا تنشر قائمة كاملة بالمرفقات ولا تحدد شهادة أيزو لنظام إدارة كشرط إلزامي. تحقّق مع الشركة من متطلبات نشاطك.",
          },
          {
            en: "Make each capability easy to check. Identify the products you supply or work you perform, your delivery location, relevant experience and the person responsible for enquiries. If you use subcontractors or represent a manufacturer, explain the arrangement and keep supporting evidence ready.",
            ar: "اجعل كل قدرة تذكرها قابلة للتحقق. حدّد المنتجات التي تورّدها أو الأعمال التي تنفّذها، ومكان التسليم أو التنفيذ، وخبراتك ذات الصلة، والشخص المسؤول عن الاستفسارات. وإذا كنت تستعين بمقاولين ثانويين أو تمثّل مصنعاً، فوضّح الترتيب وجهّز ما يثبته.",
          },
        ],
      },
      {
        heading: {
          en: "Review business integrity and HSSE alongside the company profile",
          ar: "راجع نزاهة الأعمال والصحة والسلامة والأمن والبيئة مع ملف الشركة",
        },
        paragraphs: [
          {
            en: "BGC's General Business Principles make the ability to apply its principles, or equivalent principles, relevant to supplier relationships. The linked Code of Conduct also explains expectations for dealings with BGC. Review integrity, worker treatment, safety and environmental practices alongside your commercial capabilities; a certificate alone does not explain how your company operates.",
            ar: "تربط مبادئ العمل العامة لشركة غاز البصرة علاقات الموردين بالقدرة على تطبيق مبادئها أو مبادئ مكافئة. وتوضّح مدونة قواعد السلوك المنشورة توقعات التعامل مع الشركة. راجع النزاهة ومعاملة العاملين والسلامة والممارسات البيئية إلى جانب قدراتك التجارية؛ فالشهادة وحدها لا توضّح كيفية عمل شركتك.",
          },
          {
            en: "If you already hold ISO certificates, check the legal entity, certified activity, issuing body and expiry date. IraqCert can review their relevance to your proposed scope before you decide whether additional certification work is needed.",
            ar: "إذا كانت لديك شهادات أيزو، فتحقّق من اسم الكيان القانوني والنشاط المشمول والجهة المانحة وتاريخ الانتهاء. تستطيع عراق سيرت مراجعة مدى صلتها بنطاق العمل المقترح قبل أن تقرّر حاجتك إلى أعمال إضافية للحصول على شهادات.",
          },
        ],
        links: [
          {
            label: {
              en: "Understand which ISO standard fits your business",
              ar: "تعرّف على معيار الأيزو المناسب لنشاطك",
            },
            to: "/insights/which-iso-standard-does-my-company-need",
          },
          {
            label: {
              en: "Check certified activities and sites",
              ar: "تحقّق من الأنشطة والمواقع المشمولة بالشهادة",
            },
            to: "/insights/iso-certification-scope-activities-sites-iraq",
          },
        ],
      },
      {
        heading: {
          en: "Ask IraqCert to review your BGC registration file",
          ar: "اطلب من عراق سيرت مراجعة ملف التسجيل لدى شركة غاز البصرة",
        },
        paragraphs: [
          {
            en: "Send your company name, country of registration, core products or services, and any existing BGC correspondence. Tell us whether you are making a first enquiry, responding to a document request or updating an existing profile. We can then identify missing evidence, organise the file and agree the support you need.",
            ar: "أرسل اسم شركتك وبلد تسجيلها ومنتجاتها أو خدماتها الأساسية وأي مراسلات سابقة مع شركة غاز البصرة. وضّح إن كنت تبدأ استفساراً أولياً، أو تستجيب لطلب مستمسكات، أو تحدّث ملفاً قائماً. عندها يمكننا تحديد الأدلة الناقصة وتنظيم الملف والاتفاق على الدعم الذي تحتاجه.",
          },
          {
            en: "IraqCert provides independent preparation and follow-up support. Any agreed professional fee covers that work; it does not buy BGC approval, a tender invitation or a contract.",
            ar: "تقدّم عراق سيرت دعماً مستقلاً لإعداد الملف ومتابعته. وتغطي الأتعاب المهنية المتفق عليها هذا العمل؛ ولا تشتري موافقة شركة غاز البصرة أو دعوة إلى مناقصة أو عقداً.",
          },
        ],
        links: [
          {
            label: { en: "Request a BGC file review", ar: "اطلب مراجعة ملف التسجيل" },
            to: "/contact",
          },
        ],
      },
    ],
    steps: [
      {
        title: { en: "Define the proposed supply", ar: "حدّد نطاق التوريد المقترح" },
        text: {
          en: "List the goods or services you can deliver and the evidence supporting each capability. Avoid a company profile that claims every possible activity.",
          ar: "اكتب السلع أو الخدمات التي تستطيع تقديمها، والأدلة الداعمة لكل قدرة. تجنّب ملفاً يدّعي أن الشركة تمارس جميع الأنشطة الممكنة.",
        },
      },
      {
        title: { en: "Confirm the submission instructions", ar: "أكّد تعليمات التقديم" },
        text: {
          en: "Ask BGC which route, business categories and attachments apply to your company. Keep the written instructions with your file so each requested item can be tracked.",
          ar: "اسأل شركة غاز البصرة عن مسار التقديم وفئات العمل والمرفقات المناسبة لشركتك. احتفظ بالتعليمات المكتوبة ضمن الملف لتتمكن من متابعة كل بند مطلوب.",
        },
      },
      {
        title: { en: "Prepare and review the file", ar: "جهّز الملف وراجعه" },
        text: {
          en: "IraqCert helps organise the company profile and requested evidence, checks consistency between documents and flags missing or expired items before submission.",
          ar: "تساعد عراق سيرت في تنظيم ملف الشركة والأدلة المطلوبة، وتتحقّق من اتساق المعلومات بين الوثائق، وتحدّد المستمسكات الناقصة أو المنتهية قبل التقديم.",
        },
      },
      {
        title: { en: "Submit and record the response", ar: "قدّم الطلب وسجّل الرد" },
        text: {
          en: "Use the route BGC confirms. Retain the submission record and any reference supplied; answer clarification requests against the latest version of your file.",
          ar: "استخدم المسار الذي تؤكّده شركة غاز البصرة. احتفظ بسجل التقديم وأي رقم مرجعي يُمنح لك، وأجب عن طلبات التوضيح بالاستناد إلى أحدث نسخة من ملفك.",
        },
      },
      {
        title: { en: "Maintain a usable company record", ar: "حافظ على ملف شركة جاهز للاستخدام" },
        text: {
          en: "Assign someone to monitor correspondence and document validity. Record changes to contacts, activities and supporting evidence, and ask how to update the submitted profile.",
          ar: "كلّف مسؤولاً بمتابعة المراسلات وصلاحية المستمسكات. سجّل تغييرات بيانات الاتصال والأنشطة والأدلة الداعمة، واستفسر عن طريقة تحديث الملف المقدّم.",
        },
      },
    ],
    documentsTitle: {
      en: "Documents to prepare for an IraqCert file review",
      ar: "مستمسكات تُجهَّز لمراجعة الملف لدى عراق سيرت",
    },
    documents: [
      {
        en: "Company profile: legal name, registration country, contact person and clearly defined products or services.",
        ar: "ملف تعريفي: الاسم القانوني، وبلد التسجيل، ومسؤول الاتصال، والمنتجات أو الخدمات المحددة بوضوح.",
      },
      {
        en: "Company registration and relevant licences, ready to check against BGC's instructions.",
        ar: "مستمسكات تسجيل الشركة والتراخيص ذات الصلة، لمراجعتها وفق تعليمات شركة غاز البصرة.",
      },
      {
        en: "Relevant project references, technical data and manufacturer authorisations where applicable.",
        ar: "مراجع الأعمال المماثلة، والبيانات الفنية، وتخويلات المصنّعين عند انطباقها.",
      },
      {
        en: "Existing quality, HSSE and integrity policies, plus evidence of how they are applied.",
        ar: "سياسات الجودة والصحة والسلامة والأمن والبيئة والنزاهة المتوفرة، مع أدلة تطبيقها.",
      },
      {
        en: "Existing certificates and any financial or insurance documents requested for your scope; verify validity before sharing.",
        ar: "الشهادات المتوفرة وأي وثائق مالية أو تأمينية تُطلب لنطاق عملك؛ تحقّق من صلاحيتها قبل مشاركتها.",
      },
      {
        en: "Previous BGC messages, reference numbers and outstanding document requests, if you have already started.",
        ar: "مراسلات شركة غاز البصرة السابقة والأرقام المرجعية وطلبات المستمسكات المعلّقة، إذا كنت قد بدأت الإجراءات.",
      },
    ],
    isoNeeded: [],
    sources: [
      {
        label: {
          en: "BGC supplier registration, procurement and FAQs — English",
          ar: "تسجيل الموردين والمشتريات والأسئلة الشائعة لدى شركة غاز البصرة — الإنجليزية",
        },
        url: "https://www.basrahgas.com/doing-business-with-bgc/",
      },
      {
        label: { en: "BGC supplier guidance — Arabic", ar: "العمل مع شركة غاز البصرة — العربية" },
        url: "https://www.basrahgas.com/doing-business-with-bgc/?lang=ar",
      },
      {
        label: {
          en: "BGC General Business Principles — English, revision 03A (2020)",
          ar: "مبادئ العمل العامة لشركة غاز البصرة — الإنجليزية، المراجعة 03A (2020)",
        },
        url: "https://www.basrahgas.com/wp-content/uploads/2022/11/General-Business-Principles-English.pdf",
      },
      {
        label: {
          en: "BGC Code of Conduct — English, revision 03A (2020)",
          ar: "مدونة قواعد السلوك لشركة غاز البصرة — الإنجليزية، المراجعة 03A (2020)",
        },
        url: "https://www.basrahgas.com/wp-content/uploads/2022/11/Code-of-Conduct-EN.pdf",
      },
      {
        label: {
          en: "BGC General Business Principles — Arabic, revision 03A (2020)",
          ar: "مبادئ العمل العامة لشركة غاز البصرة — العربية، المراجعة 03A (2020)",
        },
        url: "https://www.basrahgas.com/wp-content/uploads/2023/09/General_Business_Principles-AR-1.pdf",
      },
      {
        label: {
          en: "BGC Code of Conduct — Arabic, revision 03A (2020)",
          ar: "مدونة قواعد السلوك لشركة غاز البصرة — العربية، المراجعة 03A (2020)",
        },
        url: "https://www.basrahgas.com/wp-content/uploads/2023/09/Code_of_Conduct-AR.pdf",
      },
    ],
    faq: [
      {
        q: {
          en: "Can I register for more than one business category?",
          ar: "هل يمكنني التسجيل في أكثر من فئة أعمال؟",
        },
        a: {
          en: "BGC permits multiple categories and advises applicants to focus on their core business. Build each category claim around work your company can substantiate.",
          ar: "تسمح شركة غاز البصرة بفئات متعددة، وتوصي بالتركيز على نشاط الشركة الأساسي. اربط كل فئة بأعمال تستطيع شركتك إثبات قدرتها على تنفيذها.",
        },
      },
      {
        q: {
          en: "How long does BGC registration take?",
          ar: "كم يستغرق التسجيل لدى شركة غاز البصرة؟",
        },
        a: {
          en: "The public registration page gives no processing deadline. Ask BGC about your application's status; IraqCert can agree a preparation and follow-up scope without promising BGC's decision date.",
          ar: "لا تحدّد صفحة التسجيل العامة مدة للمعالجة. استفسر من الشركة عن حالة طلبك؛ ويمكن لعراق سيرت الاتفاق معك على نطاق الإعداد والمتابعة دون الوعد بتاريخ قرار الشركة.",
        },
      },
      {
        q: { en: "What should I send IraqCert to start?", ar: "ماذا أرسل إلى عراق سيرت للبدء؟" },
        a: {
          en: "Start with your company name, registration country, core offering and current stage. Include any BGC request you need help answering. We can use that information to define the document review and next steps.",
          ar: "ابدأ باسم الشركة وبلد تسجيلها ونشاطها الأساسي والمرحلة الحالية للطلب. أرفق أي طلب من شركة غاز البصرة تحتاج مساعدة في الرد عليه. نستخدم هذه المعلومات لتحديد مراجعة المستمسكات والخطوات التالية.",
        },
      },
    ],
  },
  {
    slug: "krg-mnr-avl",
    name: { en: "KRG Ministry of Natural Resources", ar: "وزارة الثروات الطبيعية بإقليم كردستان" },
    metaTitle: {
      en: "KRG MNR Approved Vendor List (AVL) Registration | IraqCert",
      ar: "تسجيل الموردين لدى وزارة الثروات الطبيعية (AVL) | عراق سيرت",
    },
    title: {
      en: "KRG MNR Approved Vendor List (AVL) Registration",
      ar: "تسجيل قائمة الموردين المعتمدين (AVL) لوزارة الثروات الطبيعية",
    },
    description: {
      en: "KRG MNR registration and category-specific AVL applications: eligibility, document checks and quality or HSE evidence. IraqCert reviews your company dossier.",
      ar: "تسجيل الشركات لدى وزارة الثروات الطبيعية وطلبات قوائم الموردين بحسب النشاط: الأهلية والوثائق وأدلة الجودة والسلامة. تراجع عراق سيرت ملف شركتك ومتطلبات فئتها.",
    },
    intro: {
      en: "MNR company registration and listing on a specific Approved Vendor List (AVL) are separate stages. Start with the company's Oilfield Service Company (OSC) registration, then address the eligible activity lists. From our operating base in Erbil, IraqCert helps companies in Iraq review their registration dossier, identify missing evidence and prepare responses. MNR decides registration and listing; assistance does not guarantee approval or work.",
      ar: "تسجيل الشركة لدى وزارة الثروات الطبيعية والإدراج في قائمة محدّدة للموردين المعتمدين (AVL) مرحلتان منفصلتان. ابدأ بتسجيل الشركة بصفتها شركة خدمات نفطية (OSC)، ثم عالج قوائم الأنشطة التي يحق لها التقدّم إليها. تساعد عراق سيرت، من مقر عملها في أربيل، الشركات في العراق على مراجعة ملف التسجيل وتحديد الأدلة الناقصة وإعداد الردود. وتقرر الوزارة التسجيل والإدراج؛ فلا تضمن المساعدة الموافقة أو الحصول على عمل.",
    },
    facts: [
      {
        label: { en: "First stage", ar: "المرحلة الأولى" },
        value: {
          en: "OSC company registration with MNR. This is separate from company incorporation and from approval for a particular AVL.",
          ar: "تسجيل الشركة لدى الوزارة بصفتها شركة خدمات نفطية. وهو منفصل عن تأسيس الشركة وعن الموافقة على إدراجها في قائمة نشاط محدّدة.",
        },
      },
      {
        label: { en: "Which AVL?", ar: "أي قائمة موردين؟" },
        value: {
          en: "Match the actual activity to the current AVL catalogue and the company's eligible OSC category.",
          ar: "طابق النشاط الفعلي مع دليل قوائم الموردين الحالي وفئة شركات الخدمات النفطية التي تستوفي شركتك شروطها.",
        },
      },
      {
        label: { en: "ISO and HSE evidence", ar: "أدلة الأيزو والصحة والسلامة والبيئة" },
        value: {
          en: "Check the current category and activity requirements. A quality or HSE evidence request is not a universal requirement for every ISO certificate.",
          ar: "راجع المتطلبات الحالية للفئة والنشاط. فطلب أدلة الجودة أو الصحة والسلامة والبيئة ليس اشتراطاً عاماً لجميع شهادات الأيزو.",
        },
      },
    ],
    sections: [
      {
        heading: {
          en: "Company registration comes before category-specific listing",
          ar: "تسجيل الشركة يسبق الإدراج بحسب فئة النشاط",
        },
        paragraphs: [
          {
            en: "Use the MNR registration portal to establish the OSC application and authorised representative. The AVL catalogue identifies separate activity codes and list types. Preparing one company file does not mean every activity is approved; review each proposed activity against the company's eligibility and the evidence requested.",
            ar: "استخدم بوابة تسجيل الوزارة لإنشاء طلب شركة الخدمات النفطية وتحديد ممثّلها المخوّل. ويبيّن دليل قوائم الموردين رموزاً مستقلة للأنشطة وأنواع القوائم. ولا يعني إعداد ملف واحد للشركة الموافقة على جميع أنشطتها؛ بل راجع كل نشاط مقترح وفق أهلية الشركة والأدلة المطلوبة.",
          },
        ],
      },
      {
        heading: {
          en: "Check the company's category before assembling the dossier",
          ar: "تحقّق من فئة الشركة قبل تجميع الملف",
        },
        paragraphs: [
          {
            en: "MNR distinguishes community, local and foreign OSC categories. Ownership, the relevant corporate registration and the activity affect eligibility. IraqCert's review starts with those facts and your current application status, so document preparation follows the applicable route.",
            ar: "تميّز الوزارة بين فئات شركات الخدمات النفطية المجتمعية والمحلية والأجنبية. وتؤثر الملكية وتسجيل الشركة ذي الصلة والنشاط في الأهلية. تبدأ مراجعة عراق سيرت بهذه المعلومات وحالة طلبك الحالية، حتى يتبع إعداد الوثائق المسار المنطبق.",
          },
          {
            en: "Foreign Supplier is a restricted category, not a general route for working in Kurdistan. It covers qualifying manufacturers or a manufacturer's sole licensed traders for Kurdistan or a wider area, supplying designated Premium Supply products from abroad without a KRG corporate registration certificate. It does not permit services on the ground. Confirm the category before treating a foreign supply enquiry as a local registration dossier.",
            ar: "فئة المورّد الأجنبي (Foreign Supplier) محدودة، وليست مساراً عاماً للعمل في إقليم كردستان. وتشمل المصنّعين المؤهلين أو التجار الحاصلين على الترخيص الحصري من المصنّع لإقليم كردستان أو منطقة أوسع، لتوريد منتجات Premium Supply المحدّدة من الخارج، من دون شهادة تسجيل شركة لدى حكومة الإقليم. ولا تتيح تقديم خدمات ميدانية داخل الإقليم. أكّد الفئة قبل التعامل مع استفسار توريد أجنبي بوصفه ملف تسجيل محلياً.",
          },
        ],
      },
      {
        heading: {
          en: "What IraqCert reviews in an MNR application file",
          ar: "ما الذي تراجعه عراق سيرت في ملف طلب الوزارة؟",
        },
        paragraphs: [
          {
            en: "We compare the company details, proposed activities and available records with the applicable request. The review identifies inconsistent names, missing records, expired evidence and unanswered questions. Where quality or HSE systems need work, we can agree preparation support separately; buying certification is not a shortcut to an AVL decision.",
            ar: "نقارن بيانات الشركة والأنشطة المقترحة والسجلات المتوفرة بالطلب المنطبق. وتحدّد المراجعة اختلاف الأسماء والسجلات الناقصة والأدلة المنتهية والأسئلة التي لم تُجب. وعندما تحتاج أنظمة الجودة أو الصحة والسلامة والبيئة إلى تطوير، يمكن الاتفاق بصورة منفصلة على دعم التحضير؛ فشراء شهادة لا يختصر قرار الإدراج.",
          },
        ],
        links: [
          {
            label: {
              en: "Review certificate scope when ISO evidence is requested",
              ar: "راجع نطاق الشهادة عندما تُطلب أدلة الأيزو",
            },
            to: "/insights/iso-certification-scope-activities-sites-iraq",
          },
        ],
      },
    ],
    steps: [
      {
        title: { en: "Registration and category review", ar: "مراجعة التسجيل والفئة" },
        text: {
          en: "We review your legal entity, ownership, activities and existing MNR status before defining the dossier work.",
          ar: "نراجع الكيان القانوني والملكية والأنشطة وحالة التسجيل الحالية لدى الوزارة قبل تحديد أعمال الملف.",
        },
      },
      {
        title: { en: "Evidence and activity matching", ar: "مطابقة الأدلة والأنشطة" },
        text: {
          en: "We help organise the requested legal, capability, quality and HSE evidence for the eligible activities and flag gaps for your team.",
          ar: "نساعد في تنظيم الأدلة القانونية وأدلة القدرات والجودة والصحة والسلامة والبيئة المطلوبة للأنشطة المؤهلة، ونحدّد النواقص لفريقك.",
        },
      },
      {
        title: { en: "Application queries and follow-up", ar: "استفسارات الطلب والمتابعة" },
        text: {
          en: "We help prepare responses to document queries within the agreed scope. MNR assesses the application; the buyer separately controls procurement opportunities.",
          ar: "نساعد في إعداد الردود على استفسارات الوثائق ضمن النطاق المتفق عليه. وتقيّم الوزارة الطلب، فيما يتحكّم المشتري بصورة مستقلة بفرص المشتريات.",
        },
      },
    ],
    documentsTitle: {
      en: "Starting points for an MNR dossier review",
      ar: "نقاط البداية لمراجعة ملف الوزارة",
    },
    documents: [
      {
        en: "Corporate registration, ownership and authorised-representative information applicable to the OSC category",
        ar: "معلومات تسجيل الشركة وملكيتها وممثّلها المخوّل المنطبقة على فئتها",
      },
      {
        en: "Actual activities and proposed AVL codes",
        ar: "الأنشطة الفعلية ورموز قوائم الموردين المقترحة",
      },
      {
        en: "Existing MNR application, status and document requests",
        ar: "طلب الوزارة الحالي وحالته وطلبات الوثائق",
      },
      {
        en: "Relevant technical, personnel, experience or financial records requested for the activity",
        ar: "السجلات الفنية أو سجلات العاملين والخبرة والمالية المطلوبة للنشاط",
      },
      {
        en: "Quality and HSE system evidence, with certificates where the specific requirement calls for them",
        ar: "أدلة أنظمة الجودة والصحة والسلامة والبيئة، مع الشهادات عندما ينص المتطلب المحدّد عليها",
      },
    ],
    isoNeeded: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 29001"],
    isoNote: {
      en: "These links explain potentially relevant standards. Confirm the evidence MNR requests for your category and activity before choosing certification work; there is no basis here for treating all four as mandatory for every applicant.",
      ar: "تشرح هذه الروابط معايير قد تكون ذات صلة. أكّد الأدلة التي تطلبها الوزارة لفئتك ونشاطك قبل اختيار أعمال منح الشهادات؛ ولا ينبغي اعتبار الشهادات الأربع إلزامية لكل متقدّم.",
    },
    sources: [
      {
        label: {
          en: "MNR OSC registration portal",
          ar: "بوابة الوزارة لتسجيل شركات الخدمات النفطية",
        },
        url: "https://www.mnronline.com/Online/Registrations/",
      },
      {
        label: {
          en: "MNR OSC category definitions",
          ar: "تعريفات فئات شركات الخدمات النفطية لدى الوزارة",
        },
        url: "https://www.mnronline.com/Online/Registrations/Categories.aspx",
      },
      {
        label: { en: "MNR AVL catalogue", ar: "دليل قوائم الموردين المعتمدين لدى الوزارة" },
        url: "https://www.mnronline.com/Online/Registrations/AVLs.aspx",
      },
      {
        label: {
          en: "Published MNR registration and AVL listing instructions",
          ar: "تعليمات الوزارة المنشورة للتسجيل والإدراج في قوائم الموردين",
        },
        url: "https://xizmet.gov.krd/sites/default/files/uploaded-laws/Registration-and-AVL-Listing-Signed.pdf",
      },
    ],
    faq: [
      {
        q: {
          en: "How do I get on the KRG MNR Approved Vendor List?",
          ar: "كيف أدخل قائمة الموردين المعتمدين لوزارة الثروات الطبيعية؟",
        },
        a: {
          en: "Start with MNR OSC registration, establish the company's eligible category and then address the relevant activity lists. IraqCert reviews the dossier and helps prepare requested evidence; MNR makes the registration and listing decisions.",
          ar: "ابدأ بتسجيل شركة الخدمات النفطية لدى الوزارة وحدّد الفئة التي تستوفي شركتك شروطها، ثم عالج قوائم الأنشطة ذات الصلة. تراجع عراق سيرت الملف وتساعد في إعداد الأدلة المطلوبة، وتتخذ الوزارة قرارات التسجيل والإدراج.",
        },
      },
      {
        q: {
          en: "Is MNR company registration the same as AVL listing?",
          ar: "هل تسجيل الشركة لدى الوزارة هو نفسه الإدراج في قائمة الموردين؟",
        },
        a: {
          en: "No. OSC registration concerns the company; AVL listing concerns a specified activity for which the company is eligible. Neither is a guarantee of a tender invitation or contract.",
          ar: "لا. يخص تسجيل شركات الخدمات النفطية الشركة نفسها، بينما يخص الإدراج نشاطاً محدّداً تستوفي الشركة شروطه. ولا يضمن أي منهما دعوة إلى مناقصة أو عقداً.",
        },
      },
      {
        q: {
          en: "What should I send for an MNR document review?",
          ar: "ماذا أرسل لمراجعة وثائق طلب الوزارة؟",
        },
        a: {
          en: "Send your company name, country and registration status, actual activities, proposed AVL codes if known, and any MNR document request. Include your target date so we can agree the preparation scope without promising an approval date.",
          ar: "أرسل اسم الشركة وبلدها وحالة تسجيلها وأنشطتها الفعلية ورموز قوائم الموردين المقترحة إن عُرفت، وأي طلب وثائق من الوزارة. اذكر موعدك المستهدف للاتفاق على نطاق التحضير دون الوعد بتاريخ للموافقة.",
        },
      },
      isoFaq,
    ],
  },
  {
    slug: "isnetworld-avetta-veriforce",
    name: { en: "ISNetworld, Avetta & Veriforce", ar: "ISNetworld و Avetta و Veriforce" },
    metaTitle: {
      en: "ISNetworld, Avetta & Veriforce Registration | IraqCert",
      ar: "التسجيل في ISNetworld و Avetta و Veriforce | عراق سيرت",
    },
    title: {
      en: "ISNetworld, Avetta & Veriforce Registration for Oil & Gas Contractors",
      ar: "التسجيل في ISNetworld و Avetta و Veriforce لمقاولي النفط والغاز",
    },
    description: {
      en: "How ISNetworld, Avetta, and Veriforce prequalification works for oil and gas contractors, what they cost and require, and how IraqCert manages your registration.",
      ar: "كيف يعمل التأهيل المسبق عبر ISNetworld و Avetta و Veriforce لمقاولي النفط والغاز، وما تكلفته ومتطلباته، وكيف تدير عراق سيرت تسجيلك.",
    },
    intro: {
      en: "International operators and their contractors use third-party pre-qualification platforms to verify supplier compliance. The main three in oil and gas are ISNetworld, Avetta, and Veriforce. IraqCert registers, completes, and maintains your account on the platform your client uses.",
      ar: "تستخدم الشركات الدولية ومقاولوها منصات تأهيل مسبق من طرف ثالث للتحقق من امتثال الموردين. وأبرزها في النفط والغاز ثلاث: ISNetworld و Avetta و Veriforce. وتُسجّل عراق سيرت حسابك على المنصة التي يستخدمها عميلك وتُكمله وتُبقيه محدّثاً.",
    },
    steps: [
      {
        title: { en: "Account setup", ar: "إنشاء الحساب" },
        text: {
          en: "We register your company on the platform your client requires.",
          ar: "نُسجّل شركتك على المنصة التي يشترطها عميلك.",
        },
      },
      {
        title: { en: "Compliance upload", ar: "رفع وثائق الامتثال" },
        text: {
          en: "We complete HSE questionnaires and upload policies, insurance, and ISO certificates.",
          ar: "نُكمل استبيانات السلامة ونرفع السياسات والتأمين وشهادات الأيزو.",
        },
      },
      {
        title: { en: "Ongoing maintenance", ar: "المتابعة المستمرة" },
        text: {
          en: "We keep the account current so you stay qualified as requirements change.",
          ar: "نُبقي الحساب محدّثاً لتظل مؤهلاً مع تغيّر المتطلبات.",
        },
      },
    ],
    documents: commonDocs,
    isoNeeded: ["ISO 9001", "ISO 14001", "ISO 45001"],
    faq: [
      {
        q: {
          en: "What is ISNetworld and how do I register?",
          ar: "ما هي ISNetworld وكيف أسجّل فيها؟",
        },
        a: {
          en: "ISNetworld is a contractor pre-qualification platform used across oil, gas, and heavy industry. You subscribe, complete an HSE and compliance profile, and upload documents. Contractors typically reach qualified status in about 30 days. IraqCert manages the registration.",
          ar: "ISNetworld منصة للتأهيل المسبق للمقاولين تُستخدم في النفط والغاز والصناعة الثقيلة. تشترك فيها، وتُكمل ملف السلامة والامتثال، وترفع الوثائق. ويصل المقاولون عادةً إلى حالة التأهُّل خلال نحو 30 يوماً. وتُدير عراق سيرت التسجيل.",
        },
      },
      {
        q: {
          en: "What is the difference between Avetta and Veriforce?",
          ar: "ما الفرق بين Avetta و Veriforce؟",
        },
        a: {
          en: "Both are supplier and contractor pre-qualification networks used in oil and gas. Which one you need depends on which platform your client, the operator, uses. IraqCert works with all three.",
          ar: "كلاهما شبكة للتأهيل المسبق للموردين والمقاولين تُستخدم في النفط والغاز. والمنصة التي تحتاجها تعتمد على ما يستخدمه عميلك، الجهة المُشغِّلة. وتعمل عراق سيرت مع الثلاث جميعاً.",
        },
      },
      isoFaq,
    ],
  },
  {
    slug: "saudi-aramco-ariba",
    name: { en: "Saudi Aramco", ar: "أرامكو السعودية" },
    metaTitle: {
      en: "Saudi Aramco Supplier Registration & IKTVA | IraqCert",
      ar: "تسجيل موردي أرامكو السعودية و IKTVA | عراق سيرت",
    },
    title: {
      en: "Saudi Aramco Supplier Registration (SAP Ariba & IKTVA)",
      ar: "تسجيل موردي أرامكو السعودية (SAP Ariba و IKTVA)",
    },
    description: {
      en: "How to register as a Saudi Aramco supplier through SAP Ariba, meet the ISO and IKTVA requirements, and reach Aramco's approved vendor list. IraqCert manages it.",
      ar: "كيفية التسجيل كمورد لأرامكو السعودية عبر SAP Ariba، واستيفاء متطلبات الأيزو و IKTVA، والوصول إلى قائمة الموردين المعتمدين لدى أرامكو. تتولى عراق سيرت العملية.",
    },
    intro: {
      en: "Saudi Aramco onboards suppliers through the SAP Ariba platform and measures in-kingdom contribution through IKTVA. Registration expects accredited ISO certificates, financial and technical evidence, and HSE documentation. IraqCert prepares your quality systems and accredited certificates, then completes the Ariba registration.",
      ar: "تُسجّل أرامكو السعودية مورديها عبر منصة SAP Ariba وتقيس المساهمة المحلية عبر برنامج IKTVA. ويتطلب التسجيل شهادات أيزو معتمدة وأدلة مالية وفنية ووثائق للصحة والسلامة. وتُعدّ عراق سيرت أنظمة الجودة والشهادات المعتمدة، ثم تُكمل التسجيل في Ariba.",
    },
    platform: {
      name: "SAP Ariba",
      note: {
        en: "SAP Ariba is Aramco's supplier onboarding platform. You create an account, complete the supplier registration questionnaire, and submit your ISO and compliance documents.",
        ar: "SAP Ariba منصة أرامكو لتسجيل الموردين. تُنشئ حساباً، وتُكمل استبيان تسجيل المورد، وتُقدّم شهادات الأيزو ووثائق الامتثال.",
      },
    },
    steps: [
      {
        title: { en: "Ariba account", ar: "حساب Ariba" },
        text: {
          en: "We register your company on SAP Ariba and build the supplier profile.",
          ar: "نُسجّل شركتك في SAP Ariba ونبني ملف المورد.",
        },
      },
      {
        title: { en: "ISO and compliance", ar: "الأيزو والامتثال" },
        text: {
          en: "We prepare accredited ISO certificates and the financial, technical, and HSE documents.",
          ar: "نُعدّ شهادات الأيزو المعتمدة والوثائق المالية والفنية ووثائق السلامة.",
        },
      },
      {
        title: { en: "Registration and follow-up", ar: "التسجيل والمتابعة" },
        text: {
          en: "We complete the registration questionnaire and follow up toward approved-supplier status.",
          ar: "نُكمل استبيان التسجيل ونتابع نحو حالة المورد المعتمد.",
        },
      },
    ],
    documents: commonDocs,
    isoNeeded: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 29001"],
    areaServed: [{ type: "Country", name: "Saudi Arabia" }],
    faq: [
      {
        q: {
          en: "How do I register as a Saudi Aramco supplier?",
          ar: "كيف أسجّل كمورد لأرامكو السعودية؟",
        },
        a: {
          en: "Suppliers register through Aramco's SAP Ariba platform, complete the supplier registration questionnaire, and submit accredited ISO certificates and compliance documents. IraqCert sets up and completes the registration for you.",
          ar: "يُسجّل الموردون عبر منصة SAP Ariba الخاصة بأرامكو، ويُكملون استبيان تسجيل المورد، ويُقدّمون شهادات أيزو معتمدة ووثائق الامتثال. وتُنشئ عراق سيرت التسجيل وتُكمله نيابةً عنك.",
        },
      },
      {
        q: { en: "What is IKTVA?", ar: "ما هو برنامج IKTVA؟" },
        a: {
          en: "IKTVA (In-Kingdom Total Value Add) is Aramco's program that scores a supplier's local Saudi contribution. It sits alongside the ISO and compliance requirements in the supplier assessment.",
          ar: "IKTVA (القيمة المضافة الإجمالية داخل المملكة) هو برنامج أرامكو الذي يقيس مساهمة المورد المحلية في السعودية. ويأتي إلى جانب متطلبات الأيزو والامتثال في تقييم المورد.",
        },
      },
      isoFaq,
    ],
  },
];

export const vendorMap: Record<string, VendorOperator> = Object.fromEntries(
  vendors.map((v) => [v.slug, v]),
);

export const vendorSlugs = vendors.map((v) => v.slug);

export function getVendor(slug: string): VendorOperator | undefined {
  return vendorMap[slug];
}

// Map an ISO label to its service page path for cross-linking.
const isoSlug: Record<string, string> = {
  "ISO 9001": "iso-9001",
  "ISO 14001": "iso-14001",
  "ISO 45001": "iso-45001",
  "ISO 22000": "iso-22000",
  "ISO 29001": "iso-29001",
  "ISO 27001": "iso-27001",
};
export function isoServicePath(label: string): string | undefined {
  const slug = isoSlug[label];
  return slug ? `/services/${slug}` : undefined;
}
